/**
 * Webcam Feed Component
 * Displays live video from user's camera for the help desk
 */

import React, { useRef, useEffect, useState } from 'react'
import { Video, VideoOff, RotateCw } from 'lucide-react'

export default function WebcamFeed({ onVideoReady, height = '300px' }) {
  const videoRef = useRef(null)
  const [isActive, setIsActive] = useState(false)
  const [error, setError] = useState(null)
  const [hasCamera, setHasCamera] = useState(true)

  useEffect(() => {
    checkCameraAvailability()
  }, [])

  const checkCameraAvailability = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const hasVideoInput = devices.some(device => device.kind === 'videoinput')
      setHasCamera(hasVideoInput)
    } catch (err) {
      console.error('Error checking camera:', err)
      setHasCamera(false)
    }
  }

  const startCamera = async () => {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        setIsActive(true)
        onVideoReady?.(stream)
      }
    } catch (err) {
      setError(`Camera error: ${err.message}`)
      console.error('Camera access error:', err)
      setHasCamera(false)
    }
  }

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop())
      setIsActive(false)
    }
  }

  if (!hasCamera) {
    return (
      <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600 text-center">
        <VideoOff className="w-8 h-8 mx-auto mb-2 text-slate-400" />
        <p className="text-slate-400 text-sm">No camera detected on this device</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="bg-slate-800 rounded-lg border border-blue-500/20 overflow-hidden" style={{ height }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover bg-black"
        />
        {error && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-red-300 text-sm text-center px-4">{error}</div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {!isActive ? (
          <button
            onClick={startCamera}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex-1 justify-center"
          >
            <Video className="w-4 h-4" />
            Start Camera
          </button>
        ) : (
          <>
            <button
              onClick={stopCamera}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex-1 justify-center"
            >
              <VideoOff className="w-4 h-4" />
              Stop Camera
            </button>
            <button
              onClick={() => {
                stopCamera()
                setTimeout(startCamera, 500)
              }}
              className="flex items-center gap-2 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors"
              title="Restart camera"
            >
              <RotateCw className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
